"use client"

import styled from "@emotion/styled"
import { CustomBreadcrumbs } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { Link } from "@eurodrive/utils"
import { Typography } from "@mui/material"

export type SiteMapGroup = {
  title: string
  links: { name: string; url: string; target?: string }[]
}

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 64px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 32px;
  }
`

const StyledMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
  }
`

const StyledGroupsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const StyledGroupItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledLinksList = styled.ul`
  list-style: disc;
  margin: 0;
  padding-inline-start: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledLink = styled(Link)`
  color: ${sharedColors.black};
  text-decoration: underline;
  width: fit-content;

  &:hover {
    color: ${sharedColors.darkGreen};
  }

  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

export default function SiteMap({
  groups,
  locale,
}: {
  groups: SiteMapGroup[]
  locale: string
}) {
  const breadcrumbs = {
    selfObject: {
      title: locale === "he" ? "מפת האתר" : "Site map",
      url: "/site-map",
    },
    parents: [{ title: locale === "he" ? "ראשי" : "Home", url: "/" }],
  }

  return (
    <Container paddingMobile>
      <StyledLayout>
        <CustomBreadcrumbs breadcrumbs={breadcrumbs} />
        <StyledMainContainer>
          <Typography variant="h1" component="h1">
            {locale === "he" ? "מפת האתר" : "Site map"}
          </Typography>
          <StyledGroupsList>
            {groups.map((group, i) => (
              <StyledGroupItem key={i}>
                <Typography variant="h2" component="h2">
                  {group.title}
                </Typography>
                <StyledLinksList>
                  {group.links.map((link, j) => (
                    <li key={j}>
                      <StyledLink href={link.url} target={link.target}>
                        <Typography variant="body2" component="span">
                          {link.name}
                        </Typography>
                      </StyledLink>
                    </li>
                  ))}
                </StyledLinksList>
              </StyledGroupItem>
            ))}
          </StyledGroupsList>
        </StyledMainContainer>
      </StyledLayout>
    </Container>
  )
}
